/** Renders the suggested-spaces threads in the "For You" tab. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { AssistanceServiceProduct } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type ThreadNode,
	extractSourcesFromThread,
} from '@atlassian/rovo-navigation/home-threads-service';
import { ThreadCard } from '@atlassian/rovo-platform-ui-components/thread-card';
import type { CreateSpaceResponse } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { useRecommendedSpaces } from '@atlassian/rovo-spaces/services/useRecommendedSpaces';
import { CreateSpaceModal } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';
import type { SpaceSource } from '@atlassian/rovo-spaces/ui/CreateSpaceModal/types';

import type { ThreadsListQuery } from './__generated__/ThreadsListQuery.graphql';
import messages from './messages';
import { Sources } from './sources/Sources';
import { ThreadsListSkeleton } from './threads-list-skeleton';

/** Touchpoint identifier reported for spaces created from the For You tab. */
const FOR_YOU_TOUCHPOINT_SOURCE = 'rovo-for-you-tab';
/** Experience id for the ai-mate surface, forwarded to recommendation and create requests. */
const AI_MATE_EXPERIENCE_ID = 'ai-mate';

/** Delay in milliseconds before a hover event is fired, to filter out accidental hovers. */
const HOVER_DEBOUNCE_DELAY_MS = 200;

interface ThreadsListProps {
	/** The tenant cloud ID for recommendation requests. */
	cloudId: string;
	/** The Atlassian Account ID of the current user. */
	userId: string;
	/** Flat list of conversationIds attached to any source on any space (provided by `useListSpaces`).
	 * Used to hide threads from the suggestion list whose conversation has already been organized
	 * into a space. */
	spacesConversationIds: string[];
	/** Called after a new space is successfully created. The parent optimistically inserts the
	 * new space into the SpaceStore and launches into it; `sources` carries the originating
	 * thread's source(s) to seed onto the launched space. */
	onSpaceCreated: (
		space: CreateSpaceResponse,
		emojiId?: string,
		attachedConversationId?: string,
		sources?: SpaceSource[],
	) => void;
	/** Called whenever the empty/non-empty state of the threads list changes. */
	onEmpty?: (isEmpty: boolean) => void;
	/** Host product, used to build the `assistanceServiceParams` forwarded to `<CreateSpaceModal>`
	 * so the create request carries the correct `x-product` header. */
	product: AssistanceServiceProduct;
}

type ThreadsListRendererProps = ThreadsListProps & {
	threads: readonly ThreadNode[];
	onRecommendationsChanged?: () => void;
};

/**
 * Shared renderer for recommendation threads returned by either REST or GraphQL.
 *
 * Threads are filtered to hide any thread whose `id` is already present as a `conversationId`
 * on any source of any space — the user has already organized that thread, so it shouldn't
 * appear as a suggestion to create a new space from.
 */
const ThreadsListRenderer = ({
	cloudId,
	spacesConversationIds,
	onSpaceCreated,
	onEmpty,
	product,
	threads,
	onRecommendationsChanged,
}: ThreadsListRendererProps): React.JSX.Element | null => {
	const intl = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const assistanceServiceParams = useMemo(
		() => ({
			touchpointSource: FOR_YOU_TOUCHPOINT_SOURCE,
			product,
			experienceId: AI_MATE_EXPERIENCE_ID,
			siteId: cloudId,
		}),
		[product, cloudId],
	);

	// Keep this client-side filter even on the REST path. The endpoint filters converted threads
	// server-side, while this filter hides a newly created space immediately from optimistic store
	// state without waiting for backend propagation or another recommendation request.
	const threadIdsInSpaces = useMemo(() => new Set(spacesConversationIds), [spacesConversationIds]);
	const visibleThreads = useMemo(
		() => threads.filter((thread) => !threadIdsInSpaces.has(thread.id)),
		[threads, threadIdsInSpaces],
	);

	const isEmpty = visibleThreads.length === 0;

	useEffect(() => {
		onEmpty?.(isEmpty);
	}, [isEmpty, onEmpty]);

	const debouncedSendHoverEvent = useMemo(
		() =>
			debounce((threadId: string) => {
				sendAnalyticsEvent({
					actionSubject: 'threadCard',
					action: 'hovered',
					eventType: 'ui',
					attributes: {
						threadId,
					},
				});
			}, HOVER_DEBOUNCE_DELAY_MS),
		[sendAnalyticsEvent],
	);

	useEffect(() => {
		return () => {
			debouncedSendHoverEvent.cancel();
		};
	}, [debouncedSendHoverEvent]);

	const handleThreadHover = useCallback(
		(threadId: string) => {
			debouncedSendHoverEvent(threadId);
		},
		[debouncedSendHoverEvent],
	);

	const handleThreadHoverLeave = useCallback(() => {
		debouncedSendHoverEvent.cancel();
	}, [debouncedSendHoverEvent]);

	const [isCreateSpaceModalOpen, setIsCreateSpaceModalOpen] = useState(false);
	const [createSpaceInitialName, setCreateSpaceInitialName] = useState<string | undefined>(
		undefined,
	);
	const [createSpaceInitialThreadId, setCreateSpaceInitialThreadId] = useState<string | undefined>(
		undefined,
	);
	const [createSpaceInitialSources, setCreateSpaceInitialSources] = useState<
		SpaceSource[] | undefined
	>(undefined);
	const handleCreateSpaceClick = useCallback(
		(threadId: string, threadTitle: string, sources?: SpaceSource[]) => {
			sendAnalyticsEvent({
				actionSubject: 'createSpaceButton',
				action: 'clicked',
				eventType: 'ui',
				attributes: { threadId },
			});
			setCreateSpaceInitialName(threadTitle);
			setCreateSpaceInitialThreadId(threadId);
			setCreateSpaceInitialSources(sources);
			setIsCreateSpaceModalOpen(true);
		},
		[sendAnalyticsEvent],
	);

	if (isEmpty) {
		return (
			<>
				<ExperienceSuccess name={ExperienceName.THREADS_LIST_QUERY} />
			</>
		);
	}

	return (
		<Stack space="space.200">
			<ExperienceStart name={ExperienceName.THREADS_LIST_QUERY} />
			<Heading size="xxsmall" as="h4">
				{intl.formatMessage(messages.suggestedSpacesSectionHeading)}
			</Heading>
			<Box
				role="group"
				aria-label={intl.formatMessage(messages.threadsListLabel)}
				testId="threads-list"
			>
				<Stack space="space.200">
					{visibleThreads.map((thread) => {
						const threadId = thread.id;
						const sources = thread.sources ?? [];

						// Build the create-space sources using the shared helper (also used by the
						// Recommended Spaces panel and the side-nav recommended card) so first-party
						// sources keep their real ARI, third-party sources fall back to the URL, and
						// `sourceType`/`name`/`conversationIds` are resolved identically.
						const spaceSources =
							sources.length > 0 ? extractSourcesFromThread(sources).spaceSources : undefined;
						const modalSources: SpaceSource[] | undefined =
							spaceSources && spaceSources.length > 0 ? spaceSources : undefined;

						return (
							<Box
								key={threadId}
								onMouseEnter={() => handleThreadHover(threadId)}
								onMouseLeave={handleThreadHoverLeave}
								onFocus={() => handleThreadHover(threadId)}
								onBlur={handleThreadHoverLeave}
							>
								<ThreadCard
									threadId={threadId}
									title={thread.title}
									renderIcon={null}
									renderSources={
										sources.length > 0 ? (
											<Sources sources={sources} testId="rovo-for-you-brief-sources-button" />
										) : undefined
									}
									onCreateProjectClick={(id, title) =>
										handleCreateSpaceClick(id, title, modalSources)
									}
								/>
							</Box>
						);
					})}
				</Stack>
			</Box>
			<ExperienceSuccess name={ExperienceName.THREADS_LIST_QUERY} />
			<CreateSpaceModal
				isOpen={isCreateSpaceModalOpen}
				assistanceServiceParams={assistanceServiceParams}
				onClose={() => {
					setIsCreateSpaceModalOpen(false);
					setCreateSpaceInitialName(undefined);
					setCreateSpaceInitialThreadId(undefined);
					setCreateSpaceInitialSources(undefined);
				}}
				onCreated={(space, emojiId, attachedConversationId) => {
					onSpaceCreated(space, emojiId, attachedConversationId, createSpaceInitialSources);
					onRecommendationsChanged?.();
				}}
				cloudId={cloudId}
				initialName={createSpaceInitialName}
				initialConversationId={createSpaceInitialThreadId}
				initialSources={createSpaceInitialSources}
			/>
		</Stack>
	);
};

/** Legacy GraphQL data source retained as the `rovo_space_recommended` fallback. */
const GraphQLThreadsList = (props: ThreadsListProps): React.JSX.Element => {
	const [threadsFetchKey, setThreadsFetchKey] = useState(0);

	const data = useLazyLoadQuery<ThreadsListQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- source fields consumed structurally by Sources and extractSourcesFromThread */
		graphql`
			query ThreadsListQuery(
				$cloudId: ID!
				$experience: String!
				$homeThreadsInput: ConvoAiHomeThreadsInput!
			) {
				convoai_homeThreads(
					cloudId: $cloudId
					experience: $experience
					homeThreadsInput: $homeThreadsInput
				) {
					edges {
						node {
							id
							title
							sources {
								__typename
								... on ConvoAiHomeThreadsFirstPartySource {
									ari
									url
									data {
										__typename
									}
								}
								... on ConvoAiHomeThreadsThirdPartySource {
									url
								}
							}
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			cloudId: props.cloudId,
			experience: 'home-threads',
			homeThreadsInput: { userId: props.userId },
		},
		{
			fetchPolicy: 'store-or-network',
			fetchKey: threadsFetchKey,
		},
	);

	const threads = useMemo<ThreadNode[]>(
		() =>
			(data.convoai_homeThreads?.edges ?? []).flatMap((edge) => {
				const node = edge.node;
				if (!node?.id || !node.title) {
					return [];
				}

				return [
					{
						id: node.id,
						title: node.title,
						sources:
							node.sources?.filter(
								(source): source is NonNullable<typeof source> => source != null,
							) ?? null,
					},
				];
			}),
		[data],
	);

	const handleRecommendationsChanged = useCallback(() => {
		setThreadsFetchKey((previousKey) => previousKey + 1);
	}, []);

	return (
		<ThreadsListRenderer
			{...props}
			threads={threads}
			onRecommendationsChanged={handleRecommendationsChanged}
		/>
	);
};

/** REST data source used when `rovo_space_recommended` is enabled. */
const RESTThreadsList = (props: ThreadsListProps): React.JSX.Element => {
	const { threads, isLoading, error } = useRecommendedSpaces({
		cloudId: props.cloudId,
		product: 'rovo',
		experienceId: AI_MATE_EXPERIENCE_ID,
	});

	if (error) {
		throw error;
	}

	if (isLoading) {
		return <ThreadsListSkeleton />;
	}

	return <ThreadsListRenderer {...props} threads={threads} />;
};

/** Selects the recommendation data source while preserving the legacy fallback. */
export const ThreadsList = (props: ThreadsListProps): React.JSX.Element =>
	fg('rovo_space_recommended') ? <RESTThreadsList {...props} /> : <GraphQLThreadsList {...props} />;
